import InfoIcon from '@mui/icons-material/Info';
import { Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import walletCoin from '../assets/customicons/walletCoin.png';
import { AppContext } from '../Base';
import RewardCard from '../components/cards/RewardCard';
import RewardInfoModal from '../components/infomodals/RewardInfoModal';
import AppHeader from "../components/nav/AppHeader";
import BottomNav from '../components/nav/BottomNav';
import { sendReactGAPageView } from '../consts/helpers';
import { rewards } from '../consts/rewards';
import useAuthUser from '../hooks/useAuthUser';

export default function RewardPage() {
	const { pathname } = useLocation();
	useEffect(() => {
        sendReactGAPageView(pathname, 'Rewards');
    }, [pathname])

    var { userid, checkAuth }  = useAuthUser();
    const actualCoins = useContext(AppContext).coinsState; 

    // Flags
    const [isLoading, setIsLoadingRewards] = useState(true);
    const [isSuccessful, setSuccessfulRewards] = useState(true);
    const [claimIsMade, setClaimMade] = useState(false);

    // Variables
    const [rewardsCanBeClaimed, setAvailableNow] = useState({});
    const [rewardsAimedTowardsClaiming, setAvailableLater] = useState({});
    const [toBeClaimed, setToBeClaimed] = useState({});
    const [alreadyClaimed, setClaimed] = useState({});

    // Handle tab view changes
    const [view, setView] = useState('all'); 
    const handleViewChange = (newView) => {
        setView(newView);
    };

    // Handle info popup
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const handleOpenModal = () => {
        setInfoModalOpen(true);
    };
    const handleCloseModal = () => {
        setInfoModalOpen(false);
    };

    const fetchRewards = () => {
        setIsLoadingRewards(true);
        const url = process.env.REACT_APP_REWARD_LAMBDA;
        fetch(url, {
            method: 'POST',
            body: JSON.stringify({
                "httpMethod": "GET",
                "resourcePath": "/rewards",
                "uid": userid
            }),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        }).then(response => {
            return response.json();
        }).then(data => {
            let results = JSON.parse(data?.body || '{}');
            // console.log(results)
            setSuccessfulRewards(results?.isSuccess);

            // TODO NOTE: results?.isAvailable is currently unused
            let toBeClaimedRewardsIDs = results?.toBeClaimed.map(obj => obj.rid);
            setToBeClaimed(rewards.filter(reward => toBeClaimedRewardsIDs?.includes(reward.id)));
            let alreadyClaimedRewardsIDs = results?.isAlreadyClaimed.map(obj => obj.rid);
            setClaimed(rewards.filter(reward => alreadyClaimedRewardsIDs?.includes(reward.id)));
            
            let allRewards = rewards.filter(
                reward => !toBeClaimedRewardsIDs?.includes(reward.id) && !alreadyClaimedRewardsIDs?.includes(reward.id)
            );
            setAvailableNow(allRewards.filter(reward => actualCoins >= reward.coins))
            setAvailableLater(allRewards.filter(reward => actualCoins < reward.coins))

        }).catch(error => {
            console.error('There has been a problem with your fetch operation:', error);
            return {};
        }).finally(() => {
            setIsLoadingRewards(false);
            setClaimMade(false);
        });
    }
    
    useEffect(() => {
        if (userid) fetchRewards();
    }, [userid]);

    useEffect(() => {
        if (claimIsMade) {
            fetchRewards();
            checkAuth();
        }
    }, [claimIsMade])

    return (
        <>
            <AppHeader />
            <Box className="spread-x">
                <Typography variant="h5">Rewards Store</Typography>
                <Box className='centered-x'>
                    <Typography variant='body2' sx={{ display: 'flex', alignItems: 'center' }}>
                        {actualCoins}&ensp;
                    </Typography>
                    <img src={walletCoin} alt="award-point" style={{ width: 28 }} />
                </Box>
            </Box>
            {
                isLoading ?
                    <Box className='centered-container' sx={{ flex: 1 }}>
                        <CircularProgress color='secondary' />
                    </Box>
                :
                isSuccessful ?
                    <Box sx={{ my: 2 }}>
                    {/* Toggle Buttons */}
                    <Box className="spread-x">
                        <Button 
                            onClick={() => handleViewChange('all')}
                            className={`challenge_btn ${view === 'all' ? 'active' : ''}`}
                            sx={{ flexGrow: 1, mx: 1, textAlign: 'center' }}
                        >
                            All Rewards
                        </Button>
                        <Button 
                            onClick={() => handleViewChange('claims')}
                            className={`challenge_btn ${view === 'claims' ? 'active' : ''}`}
                            sx={{ flexGrow: 1, mx: 1, textAlign: 'center' }}
                        >
                            Your Claims
                        </Button>
                    </Box>

                    { view === 'all' ? (
                        <Box>
                            {/* Can Be Claimed Rewards */}
                            <Typography variant="body1" gutterBottom sx={{mt: 2}}>
                                Rewards You Can Claim Now
                            </Typography>
                            <RewardsList
                                rewardsList={rewardsCanBeClaimed}
                                emptyListMessage={"Insufficient coin balance. Complete some challenges to earn more coins first!"}
                                uid={userid}
                                isClickable={true}
                                isClaimable={true}
                                isAlreadyClaimed={false}
                                setClaimMade={setClaimMade}
                            />

                            {/* Aimed Towards Claiming */}
                            <Box sx={{ marginTop: 4 }}>
                                <Typography variant="body1" gutterBottom>
                                    Rewards to Aim Towards
                                </Typography>
                                <RewardsList
                                    rewardsList={rewardsAimedTowardsClaiming}
                                    emptyListMessage={"There are no rewards available at the moment."}
                                    uid={userid}
                                    isClickable={true}
                                    isClaimable={false}
                                    isAlreadyClaimed={false}
                                    setClaimMade={setClaimMade}
                                />
                            </Box>
                        </Box>
                    ) : (
                        <Box>
                            <Box sx={{ marginTop: 2 }}>
                                <Box className="spread-x">
                                    <Typography variant="body1" gutterBottom>
                                        Rewards To be Collected
                                    </Typography>
                                    {/* Clickable Info Icon */}
                                    <IconButton onClick={handleOpenModal}>
                                        <InfoIcon />
                                    </IconButton>
                                    <RewardInfoModal open={infoModalOpen} handleClose={handleCloseModal} />
                                </Box>
                                <RewardsList
                                    rewardsList={toBeClaimed}
                                    emptyListMessage={"You have no rewards to be collected."}
                                    uid={userid}
                                    isClickable={false}
                                    isClaimable={false}
                                    isAlreadyClaimed={false}
                                    setClaimMade={setClaimMade}
                                />
                            </Box>

                            {/* Claimed Section */}
                            <Box sx={{ marginTop: 4 }}>
                                <Typography variant="body1" gutterBottom>
                                    Rewards Claimed
                                </Typography>
                                <RewardsList
                                    rewardsList={alreadyClaimed}
                                    emptyListMessage={"You have not claimed any rewards yet."}
                                    uid={userid}
                                    isClickable={false}
                                    isClaimable={false}
                                    isAlreadyClaimed={true}
                                    setClaimMade={setClaimMade}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
                :
                <Box className='centered-container' sx={{ flex: 1, textAlign: 'center', gap: 2 }}>
                    <Typography variant="h6">
                        Sorry! 
                    </Typography>
                    <Typography variant="body1">
                        Our rewards store is currently unavailable. Please come back another time.
                    </Typography>
                    <img src={walletCoin} alt="award-point" style={{ width: 100 }} />
                </Box>
            }
            <BottomNav />
        </>
    );
}

const RewardsList = ({ rewardsList, emptyListMessage, ...props }) => {
    if (rewardsList.length > 0) return (
        <Stack spacing={3}>
            {rewardsList.map((reward, index) => (
                <RewardCard
                    rid={reward.id} 
                    key={index}
                    imageUrl={reward.imageUrl}
                    title={reward.title}
                    description={reward.description}
                    coins={reward.coins}
                    {...props}
                />
            ))}
        </Stack>
    ) 
    else return (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            { emptyListMessage }
        </Typography>
    )
}